import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
   Modal,
   ModalHeader,
   ModalBody,
   InputGroup,
   InputGroupAddon,
   Input,
   Row, Col, Button,
} from 'reactstrap';
import Chip from '@material-ui/core/Chip';
import { UpwardCheckbox } from 'ui';

const SearchableListingModal = (props) => {
   const [selectedItems, setSelectedItems] = useState([]);
   const [query, setQuery] = useState('');
   const [isAllSelected, setIsAllSelected] = useState(false);
   const [isListFetched, setIsListFetched] = useState(false);
   const [resourceList, setResourceList] = useState({});
   const [filteredResourceList, setFilteredResourceList] = useState({});

   // Fetch From Remote
   useEffect(() => {
      if (!isListFetched && !props.rawResourceData) {
         props.fetchResource(props.fetchResourcePayload).then((resp) => {
            if (resp) {
               const formattedData = props.resourceMiddleWare(resp.data);
               setResourceList(formattedData);
               setFilteredResourceList(formattedData);
            }
         });
         setIsListFetched(true);
      }
   }, [isListFetched, setResourceList, setFilteredResourceList]);

   // Load From Props
   useEffect(() => {
      if (!isListFetched) {
         const formattedData = props.resourceMiddleWare(props.rawResourceData);
         setResourceList(formattedData);
         setFilteredResourceList(formattedData);
      }
   }, [props.rawResourceData, isListFetched, setResourceList, setFilteredResourceList]);

   const filterResourceList = (filterQuery) => {
      const newfilteredResourceList = {
         categories: { resourceList: [...resourceList.categories] },
      };
      newfilteredResourceList[resourceList.categories[0]] = resourceList[resourceList.categories[0]].filter((resource) => (
         resource.value.includes(filterQuery)
      ));
      newfilteredResourceList[resourceList.categories[1]] = resourceList[resourceList.categories[1]].filter((resource) => (
         resource.value.includes(filterQuery)
      ));
      newfilteredResourceList[resourceList.categories[2]] = resourceList[resourceList.categories[2]].filter((resource) => (
         resource.value.includes(filterQuery)
      ));
      return newfilteredResourceList;
   };

   useEffect(() => {
      if (query.length > 1) {
         setFilteredResourceList(filterResourceList(query));
      }
   }, [query, setFilteredResourceList]);

   const handleDeleteChip = useCallback((id) => {
      setSelectedItems(selectedItems.filter((item) => (item.id) !== id));
   }, [selectedItems, setSelectedItems]);

   const handleToggle = () => {
      props.toggle(selectedItems);
   };

   useEffect(() => {
      setSelectedItems(props.selectedValuesFromField);
      if (+(props.selectedValuesFromField[0]?.id) === -1) {
         setIsAllSelected(true);
      } else {
         setIsAllSelected(false);
      }
   }, [props.selectedValuesFromField, setSelectedItems]);

   const handleSelectAll = () => {
      if (isAllSelected) {
         setIsAllSelected(false);
         handleDeleteChip(-1);
         setSelectedItems([]);
      } else {
         setIsAllSelected(true);
         setSelectedItems([{ id: -1, label: `All ${ props.title }`, value: `All ${ props.title }` }]);
      }
   };

   const isResourceSelected = (resourceId) => {
      for (let i = 0; i < selectedItems.length; i++) {
         if (selectedItems[i]?.id === `${ resourceId }`) {
            return true;
         }
      }
      return false;
   };

   const handleSelect = (resource) => {
      if (isResourceSelected(resource.id)) {
         handleDeleteChip(resource.id);
      } else {
         setSelectedItems([...selectedItems, resource]);
      }
   };

   const getCheckBoxList = useCallback((resources) => {
      return resources?.map((resource) => (
         <UpwardCheckbox
            value={isResourceSelected(resource.id)}
            label={resource.label}
            id={resource.id}
            key={`checkbox-${ resource.id }`}
            onChange={() => handleSelect(resource)}
            disabled={isAllSelected}
         />
      ));
   }, [filteredResourceList, selectedItems]);

   return (
      <Modal isOpen={props.isOpen} toggle={handleToggle}>
         <ModalHeader toggle={handleToggle}>
            { props.title }
         </ModalHeader>
         <ModalBody className='container'>
            <InputGroup className='my-3'>
               <Input
                  placeholder={`Search for a ${ props.resource }`}
                  value={query}
                  onChange={(e) => { setQuery(e.target?.value); }}
               />
               <InputGroupAddon addonType='append'>
                  <button className='btn btn-dark-orange' type='button'>
                     <i className='fa fa-search'/>
                  </button>
               </InputGroupAddon>
            </InputGroup>
            {/* SELECTED KEYWORD CHIPS */ }
            {props.showPills && (
               <div className='keyword-chips-container mt-2'>
                  {
                     selectedItems && selectedItems.map(
                        ({ id, label }) => (
                           <Chip
                              key={`chip-${ id }`}
                              label={label}
                              onDelete={() => handleDeleteChip(id)}
                           />
                        ),
                     )
                  }
               </div>
            )}
            {/* SPECIAL CHECKBOX */ }
            <Col xs={ 10 } className='mx-auto my-3'>
               <UpwardCheckbox
                  value={isAllSelected}
                  label={`All ${ props.title }`}
                  id='-1'
                  onChange={handleSelectAll}
               />
            </Col>
            <Row className='mx-2'>
               <Col xs={12} md={6} lg={4}>
                  <h5>{filteredResourceList?.categoryTitles && filteredResourceList?.categoryTitles[0]}</h5>
                  {getCheckBoxList(filteredResourceList[resourceList?.categories && resourceList?.categories[0]])}
               </Col>
               <Col xs={12} md={6} lg={4}>
                  <h5>{filteredResourceList?.categoryTitles && filteredResourceList?.categoryTitles[1]}</h5>
                  {getCheckBoxList(filteredResourceList[resourceList?.categories && resourceList?.categories[1]])}
               </Col>
               <Col xs={12} md={6} lg={4}>
                  <h5>{filteredResourceList?.categoryTitles && filteredResourceList?.categoryTitles[2]}</h5>
                  {getCheckBoxList(filteredResourceList[resourceList?.categories && resourceList?.categories[2]])}
               </Col>
            </Row>
         </ModalBody>
         <Row className='justify-content-center mb-3'>
            <Button className='btn-dark-orange btn-rounded px-4' onClick={handleToggle}>
               Select
            </Button>
         </Row>
      </Modal>
   );
};

SearchableListingModal.propTypes = {
   resource: PropTypes.string.isRequired,
   title: PropTypes.string.isRequired,
   isOpen: PropTypes.bool.isRequired,
   toggle: PropTypes.func.isRequired,
   fetchResource: PropTypes.func.isRequired,
   resourceMiddleWare: PropTypes.func.isRequired,
   showPills: PropTypes.bool,
   selectedValuesFromField: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
   })).isRequired,
   fetchResourcePayload: PropTypes.objectOf(PropTypes.string),
   rawResourceData: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.bool,
   ]),
};

SearchableListingModal.defaultProps = {
   showPills: false,
   fetchResourcePayload: {},
   rawResourceData: false,
};

export default SearchableListingModal;
